import {
  CANCEL_CRAWLING,
  STOP_CRAWLING,
} from '@components/App/SystemDialogs/constants'
import { useCrawlRedirect } from '@components/CrawlRedirectDialog'
import { openSystemDialog } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useUnmount } from 'react-use'

import { useCrawler } from '@/hooks'
import { crawlerClient } from '@/services/crawler'
import type { CrawlResult } from '../../types'
import { CrawlProgress } from './components'

interface Props {
  // The page cap the crawler applies: the owner's plan decides it, not the plan
  // of the person who pressed.
  pro: boolean
  reportId: string
  saving: boolean
  siteUrl: string
  onCancel: () => void
  onError: (message: string) => void
  onFinished: (crawl: CrawlResult) => void
}

// The crawler stops whatever job a key already had before starting a new one,
// so a re-crawl gets a key of its own per report: sharing the person's key would
// kill the import they are watching in the editor.
const crawlerKey = (reportId: string) => `report-${reportId}`

// Runs the crawl the refresh button asked for, in this tab, and shows it moving.
// The crawl is the same one the import modal runs — the pages it finds are sent
// to the report at the end instead of becoming a sitemap.
//
// Mounted per run and keyed by it, so a second re-crawl starts on a fresh hook
// rather than reading the finished state of the first.
const CrawlRunnerRun: React.FC<
  Props & { onRedirect: (url: string) => void }
> = ({
  pro,
  reportId,
  saving,
  siteUrl,
  onCancel,
  onRedirect,
  onError,
  onFinished,
}) => {
  const dispatch = useDispatch()

  const endpoint = new URL(`${import.meta.env.VITE_GENERATOR_URL}/jobs/run`)

  endpoint.searchParams.set('url', siteUrl)
  endpoint.searchParams.set('userId', crawlerKey(reportId))
  endpoint.searchParams.set('meta', 'true')
  endpoint.searchParams.set('confirmRedirect', 'true')

  if (pro) {
    endpoint.searchParams.set('pro', 'true')
  }

  const { close, done, error, extra, id, issues, progress, redirect, result } =
    useCrawler({ url: endpoint.toString() })

  useCrawlRedirect(redirect, onRedirect, onCancel)

  const pages = [...result, ...issues]

  useEffect(() => {
    if (done) {
      onFinished({
        // This target changes only after a confirmed cross-domain redirect.
        // A same-domain landing path must not narrow subsequent recrawls.
        url: siteUrl,
        faviconUrl: extra?.icon ?? null,
        pages,
        title: extra?.title ?? null,
      })
    }
  }, [done])

  useEffect(() => {
    if (error) {
      onError(error)
    }
  }, [error])

  useUnmount(() => {
    close()
  })

  // The crawl keeps running on the crawler after the stream is closed, so it is
  // told to stop as well — otherwise it works its way through the whole site
  // with nobody left to read the answer.
  const abortJob = () => {
    if (id) {
      crawlerClient
        .cancelActiveJob(id, crawlerKey(reportId))
        .catch(() => undefined)
    }

    close()
  }

  // Both ways out of a crawl throw something away — the pages it has not
  // reached, or the whole run — so both ask first, through the same dialogs the
  // import crawler uses.
  const confirm = (dialog: string) =>
    new Promise<boolean>((resolve) => {
      dispatch(openSystemDialog(dialog, { callback: resolve }))
    })

  // Stopping keeps what the crawl has found so far, which is only worth writing
  // over the report when it found anything at all.
  const handleStop = async () => {
    const confirmed = await confirm(STOP_CRAWLING)

    if (!confirmed) {
      return
    }

    abortJob()

    if (!result.length) {
      onCancel()

      return
    }

    onFinished({
      url: siteUrl,
      faviconUrl: extra?.icon ?? null,
      pages: result,
      title: extra?.title ?? null,
    })
  }

  const handleCancel = async () => {
    const confirmed = await confirm(CANCEL_CRAWLING)

    if (!confirmed) {
      return
    }

    abortJob()
    onCancel()
  }

  return (
    <CrawlProgress
      percent={progress.progressPercentage}
      saving={saving}
      scanned={progress.scanned}
      total={progress.totalCount}
      url={progress.url}
      onCancel={handleCancel}
      onStop={handleStop}
    />
  )
}

export const CrawlRunner: React.FC<Props> = (props) => {
  const [siteUrl, setSiteUrl] = useState(props.siteUrl)

  return (
    <CrawlRunnerRun
      {...props}
      key={siteUrl}
      siteUrl={siteUrl}
      onRedirect={setSiteUrl}
    />
  )
}
